import {
  CadetReader,
  CadetWriter,
  LayoutOrientation,
  newIdentifier,
  NodeType,
  CadetDoc as SourceCadetDoc,
  DSLClasses as SourceClasses,
  type Identifier
} from '@cadet-dsl';
import { Path, Point, Size, type Rect } from '@geom';
import { Compound, Elements, type Edges, type EID, type NID, type Nodes } from '@graph';
import type { ImageSource } from '@image';
import {
  LayoutConstants,
  SpatialIndex,
  SpatialIndexItemType,
  type SpatialIndexItem
} from '@layout';
import { DEFAULT_LOCALE_ID, IString, letterCode, LocaleId } from '@strings';
import {
  assertInstanceOf,
  expectDefined,
  immerable,
  immerClone,
  produce,
  produceWithPatches,
  type Draft
} from '@utils';
import type { VisualCache } from '@visual';
import { DocClasses } from './DocClasses';
import { DocLayout } from './DocLayout';
import { EdgeLayout } from './EdgeLayout';
import { EdgeSpec } from './EdgeSpec';
import { GroupSpec } from './GroupSpec';
import { makeGroupLayout, makeUnitLayout } from './Layouter';
import { NodeLayout } from './NodeLayout';
import type { NodeSpec } from './NodeSpec';
import { UnitSpec } from './UnitSpec';

export enum ImageLoadStatus {
  STARTED = 'started',
  LOADED = 'loaded',
  SKIPPED = 'skipped',
  ERROR = 'error'
}

/**
 * Represents the runtime state of a CADET document.
 * Designed for use with immer for immutable state management.
 */
export class Doc {
  [immerable] = true;

  // --- Core State Properties ---
  id: Identifier;
  graph: Compound<unknown, NodeSpec, EdgeSpec>;
  layout: DocLayout;
  classes: DocClasses;
  hasLayout = false;
  images = new Map<string, ImageSource>();
  // Declared include symbols (e.g., ['prelude']) preserved from parsed DSL
  private _includes: string[] = [];
  // Map of className -> includeSymbol; preserved across load/save
  includeClassOrigins = new Map<string, string>();

  // --- Document Settings (Direct Properties) ---
  orientation: LayoutOrientation = LayoutOrientation.UNCONSTRAINED;
  alignRoots = false;
  alignLeaves = false;
  bundleEdges = true;
  locale = DEFAULT_LOCALE_ID;
  // Add any other document-level settings here

  /* ------------------------------------------------------------------ *
   *  Immer mutation helper (undo/redo)                                 *
   * ------------------------------------------------------------------ */
  /**
   * Returns a new Doc with changes applied inside the callback.
   * The `reason` string is purely for debugging.
   */
  produce(reason: string, fn: (draft: Draft<this>) => void): this {
    return produce(this, (draft: Draft<this>) => {
      draft.id = newIdentifier();
      fn(draft);
    });
  }

  produceWithPatches(reason: string, fn: (draft: Draft<this>) => void) {
    return produceWithPatches(this, (draft: Draft<this>) => {
      draft.id = newIdentifier();
      fn(draft);
    });
  }

  /**
   * Creates a new, empty Doc instance with default settings and classes.
   */
  constructor() {
    this.id = newIdentifier();
    this.graph = new Compound<unknown, NodeSpec, EdgeSpec>();
    this.layout = new DocLayout();
    // Initialize with default/empty class hierarchy (CadetDoc constructor does this)
    this.classes = new DocClasses(new SourceClasses());
    // Settings are initialized directly above via property initializers
  }

  // // --- Load/Save ---
  /** Parses a CADET-DSL string and returns a fully populated runtime `Doc`.
   *  Any `ParseError` (or other exception) is propagated to the caller. */
  static load(dslString: string, cache: VisualCache): Doc {
    performance.mark('Doc:load:start');
    // 0. Parse the DSL ─ this will throw `ParseError`
    const reader = new CadetReader(dslString);
    const cadetDoc = reader.doc;

    if (reader.hasWarnings) {
      console.warn('CADET DSL parsing generated warnings:', reader.warnings);
    }

    // Create Doc and populate from cadetDoc
    const doc = new Doc();
    doc.id = cadetDoc.id; // Use ID from parsed document
    // 1. Load Classes into ClassesRuntime first
    doc.classes = new DocClasses(cadetDoc.classes); // Deep copies data
    // Preserve declared includes and origins directly; classes already include them
    doc._includes = [...cadetDoc.includes];
    doc.includeClassOrigins = new Map(cadetDoc.includeClassOrigins);

    // 2. Load Settings (Validation happens here)
    if (cadetDoc.rawSettings) {
      const settings = cadetDoc.rawSettings;
      const processedKeys = new Set<string>();
      const orientationKey = 'orientation';
      processedKeys.add(orientationKey);
      if (Object.prototype.hasOwnProperty.call(settings, orientationKey)) {
        const value = settings[orientationKey];
        if (typeof value === 'string') {
          const lowerVal = value.toLowerCase();
          if (lowerVal === 'tb') doc.orientation = LayoutOrientation.TOP_TO_BOTTOM;
          else if (lowerVal === 'bt') doc.orientation = LayoutOrientation.BOTTOM_TO_TOP;
          else if (lowerVal === 'lr') doc.orientation = LayoutOrientation.LEFT_TO_RIGHT;
          else if (lowerVal === 'rl') doc.orientation = LayoutOrientation.RIGHT_TO_LEFT;
          else doc.orientation = LayoutOrientation.UNCONSTRAINED;
        } else {
          console.warn(/* Invalid type */);
        }
      }

      const localeKey = 'locale';

      if (Object.prototype.hasOwnProperty.call(settings, localeKey)) {
        const value = settings[localeKey] ?? DEFAULT_LOCALE_ID;
        if (typeof value === 'string') {
          doc.locale = value as LocaleId;
        } else {
          console.warn(/* Invalid type */);
          doc.locale = DEFAULT_LOCALE_ID;
        }
      }

      const booleanSettings: ('alignRoots' | 'alignLeaves' | 'bundleEdges')[] = [
        'alignRoots',
        'alignLeaves',
        'bundleEdges'
      ];
      for (const key of booleanSettings) {
        processedKeys.add(key);
        if (Object.prototype.hasOwnProperty.call(settings, key)) {
          const value = settings[key];
          if (typeof value === 'boolean') doc[key] = value;
          else console.warn(/* Invalid type */);
        }
      }
      for (const _key in settings) {
        /* Warn unknown keys */
      }
    }

    // 3. Load Nodes (Create runtime NodeSpec with resolved attributes)
    const nodeDataMap = new Map<NID, NodeSpec>(); // Temp map: NID -> runtime NodeSpec
    for (const nodeId of cadetDoc.graph.nodes) {
      const sourceNodeData = cadetDoc.nodeData(nodeId); // From @cadet-dsl/CadetDoc
      // Take only instance/state attributes from the source node
      const instanceAttrs = sourceNodeData.attrs;
      // Ensure reserved keys exist and have correct types
      let title = instanceAttrs.title;
      if (title !== undefined) {
        assertInstanceOf(title, IString);
      } else {
        title = IString.EMPTY;
      }
      let note = instanceAttrs.note;
      if (note !== undefined) {
        assertInstanceOf(note, IString);
      } else {
        note = IString.EMPTY;
      }
      let assistantNote = instanceAttrs.assistantNote;
      if (assistantNote !== undefined) {
        assertInstanceOf(assistantNote, IString);
      } else {
        assistantNote = IString.EMPTY;
      }
      const finalRuntimeAttrs: Record<string, unknown> = {
        ...instanceAttrs,
        title,
        note,
        assistantNote
      };

      // Create the new runtime NodeSpec instance (from ./data)
      let runtimeNodeData: NodeSpec;
      if (sourceNodeData.type === NodeType.GROUP) {
        const title = finalRuntimeAttrs.title;
        assertInstanceOf(title, IString);
        const gd = new GroupSpec(title);
        gd.attrs = finalRuntimeAttrs;
        runtimeNodeData = gd;
      } else {
        const title = finalRuntimeAttrs.title;
        assertInstanceOf(title, IString);
        const ud = new UnitSpec(doc, sourceNodeData.cls, finalRuntimeAttrs);
        ud.attrs = finalRuntimeAttrs;
        runtimeNodeData = ud;
      }
      // Preserve the specific subclass name parsed from DSL
      runtimeNodeData.cls = sourceNodeData.cls;

      // Store temporarily before adding to graph and attaching layout
      nodeDataMap.set(nodeId, runtimeNodeData);
    }

    // Add nodes to the runtime graph
    for (const [nodeId, nodeData] of nodeDataMap.entries()) {
      doc.graph.addNode(nodeId, { data: nodeData });
    }
    // --- End Step 3 ---

    // 4. Load Edges
    // Temporary map to hold created EdgeData instances before layout attachment
    const edgeDataMap = new Map<EID, EdgeSpec>();
    for (const edgeId of cadetDoc.graph.edges) {
      const sourceEdgeData = cadetDoc.edgeData(edgeId); // From @cadet-dsl/CadetDoc
      const sourceNodeId = cadetDoc.graph.source(edgeId);
      const targetNodeId = cadetDoc.graph.target(edgeId);

      // Take only the instance/state attributes that belong to this edge
      const instanceAttrs = immerClone(sourceEdgeData.attrs);

      // Create the new runtime EdgeData instance (from ./data)
      const runtimeEdgeData = new EdgeSpec(
        sourceEdgeData.cls, // Store the specific DSL class name (e.g., "->")
        instanceAttrs // Store the resolved & cleaned attributes
      );

      // Store temporarily (useful for attaching layout later)
      edgeDataMap.set(edgeId, runtimeEdgeData);

      // Add edge to the runtime graph
      // Assumes source/target nodes were added in Step 3.
      // Compound graph handles checks for node existence.
      doc.graph.addEdge(edgeId, sourceNodeId, targetNodeId, runtimeEdgeData); // Pass runtimeEdgeData directly
    }
    // --- End Step 4 ---

    // 5. Load Layout (Nodes & Edges) - Create runtime Layout objects
    doc.hasLayout = false; // Initialize layout flag

    // --- Node Layout (:BOUNDS / MeasuredText) ---
    for (const [nodeId, sourceLayout] of cadetDoc.layout.nodes.entries()) {
      const runtimeNodeData = nodeDataMap.get(nodeId);
      const boundsFromDsl = sourceLayout.bounds; // Rect from CadetDoc layout

      if (runtimeNodeData) {
        // Create the runtime NodeLayout using the helper
        const runtimeNodeLayout = Doc._createNodeLayout(
          doc.classes, // Pass ClassesRuntime for attribute resolution
          runtimeNodeData,
          boundsFromDsl,
          cache,
          doc.locale
        );

        if (runtimeNodeLayout) {
          doc.layout.nodes.set(nodeId, runtimeNodeLayout);
          doc.hasLayout = true;
        } else {
          console.warn(`Cadet Load: Could not create layout for node '${nodeId}'.`);
        }
      } else {
        console.warn(
          `Cadet Load: Layout found for non-existent node '${nodeId}'. Skipping.`
        );
      }
    }

    // --- Edge Layout (:PATH) ---
    for (const [edgeId, sourceEdgeLayout] of cadetDoc.layout.edges.entries()) {
      const runtimeEdgeData = edgeDataMap.get(edgeId);
      const path = sourceEdgeLayout.path; // Path from CadetDoc layout

      if (runtimeEdgeData) {
        const runtimeEdgeLayout = new EdgeLayout(path);
        doc.layout.edges.set(edgeId, runtimeEdgeLayout);
        doc.hasLayout = true;
      } else {
        console.warn(
          `Cadet Load: Layout found for non-existent edge '${edgeId}'. Skipping.`
        );
      }
    }
    // --- End Step 5 ---

    // 6. Load Hierarchy
    // Reconstruct parent-child relationships using the forest from the parsed cadetDoc
    for (const nodeId of cadetDoc.graph.nodes) {
      const parentId = cadetDoc.graph.forest.parent(nodeId); // Get parent from source forest

      // If a node has a parent in the source document structure...
      if (parentId !== undefined) {
        // Check nodes exist in target graph (should generally be true)
        if (doc.graph.hasNode(nodeId) && doc.graph.hasNode(parentId)) {
          // Establish the parent-child relationship in the runtime graph's forest.
          // Need to provide a unique ID for the containment edge.
          doc.graph.moveNode(nodeId, {
            newParent: parentId,
            newEdge: newIdentifier()
          });
        } else {
          // Log warning if nodes are unexpectedly missing
          console.warn(
            `Cadet Load: Cannot establish hierarchy for node '${nodeId}' - node or parent '${parentId}' missing in target graph.`
          );
        }
      }
      // Nodes without a parent in cadetDoc.graph.forest remain at the root in doc.graph (default state).
    }
    // --- End Step 6 ---

    // 7. Transfer Images ownership (no I/O here)
    if (cadetDoc.images.size > 0) {
      for (const [imageId, src] of cadetDoc.images.entries()) {
        doc.images.set(imageId, src);
      }
    }

    performance.mark('Doc:load:end');
    performance.measure('Doc:load', 'Doc:load:start', 'Doc:load:end');
    return doc;
  }

  // --- Accessors ---
  nodeSpec(node: NID): NodeSpec {
    return this.graph.nodeData(node);
  }
  edgeSpec(edge: EID): EdgeSpec {
    return this.graph.edgeData(edge);
  }

  nodeLayout(node: NID): NodeLayout {
    return expectDefined(this.layout.nodes.get(node));
  }
  edgeLayout(edge: EID): EdgeLayout {
    return expectDefined(this.layout.edges.get(edge));
  }

  nodeBounds(node: NID): Rect {
    return this.nodeLayout(node).bounds;
  }
  edgePath(edge: EID): Path {
    return this.edgeLayout(edge).path;
  }

  // Get final resolved attribute value using ClassesRuntime
  nodeAttr(node: NID, key: string): unknown {
    return this.classes.getInstanceAttr(this.nodeSpec(node), key);
  }
  edgeAttr(edge: EID, key: string): unknown {
    return this.classes.getInstanceAttr(this.edgeSpec(edge), key);
  }

  // Convenience accessors using fail-fast assertion where appropriate
  nodeTitle(node: NID): IString {
    const note = this.nodeAttr(node, 'title');
    if (note === undefined) {
      return IString.EMPTY;
    } else {
      assertInstanceOf(note, IString);
      return note;
    }
  }

  nodeClass(node: NID): string {
    return this.nodeSpec(node).cls;
  }

  setNodeClass(node: NID, newCls: string): void {
    const isUnit = this.isUnit(node);
    if (isUnit !== this.classes.isUnitClass(newCls)) {
      throw new Error(`Cannot change node ${node} from Unit to Group or vice versa`);
    }
    if (isUnit) {
      const oldUnitSpec: UnitSpec = this.nodeSpec(node) as UnitSpec;
      const newUnitSpec = new UnitSpec(this, newCls, oldUnitSpec.attrs);
      this.graph.setNodeData(node, newUnitSpec);
    } else {
      throw new Error(`Changing group class unimplemented`);
    }
  }

  setNodeTitle(node: NID, title: IString): void {
    assertInstanceOf(title, IString);
    this.nodeSpec(node).attrs.title = title;
  }

  nodeNote(node: NID): IString {
    return (this.nodeAttr(node, 'note') as IString | undefined) ?? IString.EMPTY;
  }

  setNodeNote(node: NID, note: IString): void {
    assertInstanceOf(note, IString);
    this.nodeSpec(node).attrs.note = note;
  }

  nodeAssistantNote(node: NID): IString {
    return (this.nodeAttr(node, 'assistantNote') as IString | undefined) ?? IString.EMPTY;
  }

  setNodeAssistantNote(node: NID, note: IString): void {
    assertInstanceOf(note, IString);
    this.nodeSpec(node).attrs.assistantNote = note;
  }

  isGroup(node: NID): boolean {
    return this.nodeSpec(node).type === NodeType.GROUP;
  }

  isUnit(node: NID): boolean {
    return this.nodeSpec(node).type === NodeType.UNIT;
  }

  /** Filters a set of nodes, returning only groups. */
  groupsIn(nodes: Nodes): Nodes {
    return new Set(Array.from(nodes).filter(n => this.isGroup(n)));
  }

  /** Filters a set of nodes, returning only non-groups (units). */
  unitsIn(nodes: Nodes): Nodes {
    return new Set(Array.from(nodes).filter(n => !this.isGroup(n)));
  }

  /** Rectangle that bounds an edge’s visual path. */
  edgeBounds(edge: EID): Rect {
    return this.edgeLayout(edge).path.bounds;
  }

  /** Calculates the bounding rectangle of an arbitrary element set. */
  elementsBounds(elements: Elements): Rect | undefined {
    let result: Rect | undefined;
    for (const n of elements.nodes) {
      result = this.nodeBounds(n).union(result);
    }

    // Edges can extend beyond node bounds; include them if laid-out
    for (const e of elements.edges) {
      const layout = this.layout.edges.get(e);
      if (layout) {
        result = layout.path.bounds.union(result);
      }
    }
    return result;
  }

  /** Legacy getter – now delegates to `elementsBounds(this.elements)` */
  get bounds(): Rect | undefined {
    return this.elementsBounds(this.elements);
  }

  /** Computes the zoom + pan that best fits the document into a rect. */
  fitToRect(r: Rect): { zoom: number; pan: Point } | undefined {
    const b = this.bounds;
    if (b === undefined) return undefined;

    const fitBounds = r.shrinkingBy(LayoutConstants.PAN_ZOOM_REVEAL_PADDING);
    const zoom = b.scaleThatFits(fitBounds);
    const pan = fitBounds.center.sub(b.center.mul(zoom));
    return { zoom, pan };
  }

  /** Returns the first unused ‘A’, ‘B’… ‘AA’… style letter code. */
  firstAvailableLetterCode(): string {
    const titles = new Set(
      Array.from(this.graph.forest.nodes).map(n => this.nodeTitle(n).get())
    );

    let i = 0;
    for (;;) {
      const code = letterCode(i);
      if (!titles.has(code)) return code;
      i++;
    }
  }

  /* ------------------------------------------------------------------ *
   *  Convenience accessors for edge notes                              *
   * ------------------------------------------------------------------ */
  edgeNote(edge: EID): IString {
    const v = this.edgeAttr(edge, 'note');
    if (v === undefined) {
      return IString.EMPTY;
    } else {
      assertInstanceOf(v, IString);
      return v;
    }
  }

  edgeAssistantNote(edge: EID): IString {
    const v = this.edgeAttr(edge, 'assistantNote');
    if (v === undefined) {
      return IString.EMPTY;
    } else {
      assertInstanceOf(v, IString);
      return v;
    }
  }

  /* ------------------------------------------------------------------ *
   *  Spatial-index builder (needed by hit-testing helpers)              *
   * ------------------------------------------------------------------ */
  makeSpatialIndex(): SpatialIndex {
    const items: SpatialIndexItem[] = [];

    for (const node of this.graph.nodes) {
      const layout = this.layout.nodes.get(node);
      if (!layout) continue; // no bounds yet
      items.push({
        id: node,
        type: SpatialIndexItemType.NODE,
        bounds: layout.bounds
      });
    }

    for (const edge of this.graph.edges) {
      const layout = this.layout.edges.get(edge);
      if (!layout) continue; // not routed yet
      items.push({
        id: edge,
        type: SpatialIndexItemType.EDGE,
        bounds: layout.path.bounds.expandingBy(LayoutConstants.HOVERED_EDGE_MAX_DISTANCE)
      });
    }

    return new SpatialIndex(items);
  }

  /* ------------------------------------------------------------------ *
   *  Simple mutators mirrored from legacy Doc                           *
   * ------------------------------------------------------------------ */
  setNodeBounds(node: NID, bounds: { center: Point; size: Size }): void {
    const layout = this.nodeLayout(node);
    layout.center = bounds.center;
    layout.size = bounds.size;
  }

  offsetNodes(offset: Point): void {
    for (const n of this.graph.nodes) this.offsetNodeBounds(n, offset);
  }

  offsetNodeBounds(node: NID, offset: Point): void {
    const layout = this.nodeLayout(node);
    layout.center = layout.center.add(offset);
  }

  offsetNodeAndDescendants(node: NID, offset: Point): void {
    for (const child of this.graph.descendants(node)) {
      this.offsetNodeBounds(child, offset);
    }
    this.offsetNodeBounds(node, offset);
  }

  /**
   * Removes the given elements from the graph and returns the actually-removed
   * elements (useful for undo/redo).
   */
  deleteElements(elements: Elements): Elements {
    const removedNodes: Nodes = new Set();
    const removedEdges: Edges = new Set();

    // Delete nodes + any incident edges
    for (const n of elements.nodes) {
      if (!this.graph.hasNode(n)) continue;

      for (const inc of this.graph.incidentEdges(n)) {
        if (this.graph.hasEdge(inc)) {
          this.graph.removeEdge(inc);
          removedEdges.add(inc);
        }
      }
      this.graph.removeNodeUngrouping(n);
      removedNodes.add(n);
    }

    // Delete explicit edges
    for (const e of elements.edges) {
      if (this.graph.hasEdge(e)) {
        this.graph.removeEdge(e);
        removedEdges.add(e);
      }
    }

    return Elements.make(removedNodes, removedEdges);
  }

  /* ------------------------------------------------------------------ *
   *  Debug helper                                                       *
   * ------------------------------------------------------------------ */
  logElements(elements: Elements): void {
    console.log('nodes:');
    for (const n of elements.nodes) {
      console.log(n, this.nodeTitle(n));
    }
    console.log('edges:');
    for (const e of elements.edges) {
      const s = this.graph.source(e);
      const t = this.graph.target(e);
      console.log(e, this.nodeTitle(s), '→', this.nodeTitle(t));
    }
  }

  // Getters for overall structure
  get nodes(): Nodes {
    return this.graph.nodes;
  }
  get edges(): Edges {
    return this.graph.edges;
  }
  get elements(): Elements {
    return this.graph.elements;
  }

  // --- Other Methods ---
  /**
   * Creates the correct concrete NodeLayout (UnitLayout or GroupLayout)
   * depending on the node type.  Falls back to generic sizing if, for
   * some reason, the helpers cannot be applied.
   */
  private static _createNodeLayout(
    _classes: DocClasses,
    nodeSpec: NodeSpec,
    boundsFromDsl: Rect | undefined,
    cache: VisualCache,
    locale: LocaleId
  ): NodeLayout | undefined {
    // Preserve the center of the incoming bounds (if any) so that the
    // helper can retain the original position.
    const position = boundsFromDsl?.center;
    const size = boundsFromDsl?.size;

    switch (nodeSpec.type) {
      case NodeType.UNIT:
        return makeUnitLayout(nodeSpec as UnitSpec, position, cache, locale);

      case NodeType.GROUP:
        return makeGroupLayout(nodeSpec as GroupSpec, position, size, cache, locale);
    }
  }

  private static docToCadetDoc(doc: Doc): SourceCadetDoc {
    // 1. Instantiate intermediate CadetDoc
    const cadetDoc = new SourceCadetDoc(); // Assumes constructor handles default classes
    // Preserve declared includes so CadetWriter can re-emit them
    cadetDoc.includes = [...doc.includes];
    // Preserve include-origin class mapping so the writer can skip them
    cadetDoc.includeClassOrigins = new Map(doc.includeClassOrigins);

    // 2. Copy Settings
    const rawSettings: Record<string, unknown> = {};
    switch (doc.orientation) {
      case LayoutOrientation.TOP_TO_BOTTOM:
        rawSettings.orientation = 'TB';
        break;
      case LayoutOrientation.BOTTOM_TO_TOP:
        rawSettings.orientation = 'BT';
        break;
      case LayoutOrientation.LEFT_TO_RIGHT:
        rawSettings.orientation = 'LR';
        break;
      case LayoutOrientation.RIGHT_TO_LEFT:
        rawSettings.orientation = 'RL';
        break;
    }
    // Corrected boolean checks:
    if (doc.alignRoots) rawSettings.alignRoots = true; // Only add if true (assuming default false)
    if (doc.alignLeaves) rawSettings.alignLeaves = true; // Only add if true (assuming default false)
    if (!doc.bundleEdges) rawSettings.bundleEdges = false; // Only add if false (assuming default true)
    if (doc.locale !== DEFAULT_LOCALE_ID) rawSettings.locale = doc.locale;

    if (Object.keys(rawSettings).length > 0) {
      cadetDoc.rawSettings = rawSettings;
    }

    // 3. Copy Classes (using the new public method)
    const defaultClasses = new Set([
      'Element',
      'Node',
      'Edge',
      'Unit',
      'Group',
      'Entity',
      'Junctor',
      '->'
    ]);

    // Pull all runtime classes (excluding defaults) into an array so we
    // can order them by inheritance depth: superclasses first.
    type ClassTuple = [string, { parent?: string; attrs: Record<string, unknown> }];
    const runtimeClasses: ClassTuple[] = [];

    for (const entry of doc.classes.getAllClassDetails()) {
      const [className, details] = entry;
      if (defaultClasses.has(className)) continue; // skip built-ins
      // Skip classes that originated from includes; writer will re-emit :INCLUDE
      if (doc.includeClassOrigins.has(className)) continue;
      runtimeClasses.push([className, details]);
    }

    // Helper to compute depth (root = 0).  Cache results to avoid repeat work.
    const depthCache = new Map<string, number>();
    function depth(className: string): number {
      if (depthCache.has(className)) return expectDefined(depthCache.get(className));
      const details = runtimeClasses.find(([name]) => name === className)?.[1];
      const parentName = details?.parent;
      const d = parentName && !defaultClasses.has(parentName) ? depth(parentName) + 1 : 0;
      depthCache.set(className, d);
      return d;
    }

    // Sort by depth asc, then lexicographically for determinism.
    runtimeClasses.sort(([a], [b]) => {
      const da = depth(a);
      const db = depth(b);
      return da === db ? a.localeCompare(b) : da - db;
    });

    // Add to the intermediate CadetDoc in the resolved order.
    for (const [className, details] of runtimeClasses) {
      cadetDoc.classes.addClass(className, details.parent ?? '', {
        attrs: details.attrs
      });
    }

    // 4. Copy Graph & Hierarchy Structure (attach data objects at insertion)
    // --- nodes ---
    for (const nodeId of doc.graph.nodes) {
      const runtimeNodeData = doc.nodeSpec(nodeId);
      const cdNodeData: import('@cadet-dsl').DSLNodeData = {
        cls: runtimeNodeData.cls,
        type: runtimeNodeData.type,
        attrs: immerClone(runtimeNodeData.attrs)
      };
      cadetDoc.graph.addNode(nodeId, { data: cdNodeData });
    }

    // --- edges ---
    for (const edgeId of doc.graph.edges) {
      const source = doc.graph.source(edgeId);
      const target = doc.graph.target(edgeId);

      if (cadetDoc.graph.hasNode(source) && cadetDoc.graph.hasNode(target)) {
        const runtimeEdgeData = doc.edgeSpec(edgeId);
        const cdEdgeData: import('@cadet-dsl').DSLEdgeData = {
          cls: runtimeEdgeData.cls,
          attrs: immerClone(runtimeEdgeData.attrs)
        };
        cadetDoc.graph.addEdge(edgeId, source, target, cdEdgeData);
      } else {
        console.warn(
          `DocSave: Skipping edge '${edgeId}' due to missing source/target in intermediate graph.`
        );
      }
    }
    // --- hierarchy ---
    for (const nodeId of doc.graph.nodes) {
      const parent = doc.graph.forest.parent(nodeId);
      if (parent !== undefined) {
        if (cadetDoc.graph.hasNode(nodeId) && cadetDoc.graph.hasNode(parent)) {
          cadetDoc.graph.moveNode(nodeId, {
            newParent: parent,
            newEdge: newIdentifier()
          });
        } else {
          console.warn(
            `DocSave: Skipping hierarchy for node '${nodeId}' due to missing node/parent in intermediate graph.`
          );
        }
      }
    }

    // 5. Copy Layout (same as before)
    for (const [nodeId, runtimeNodeLayout] of doc.layout.nodes.entries()) {
      if (cadetDoc.graph.hasNode(nodeId)) {
        cadetDoc.setNodeBounds(nodeId, runtimeNodeLayout.bounds);
      }
    }
    for (const [edgeId, runtimeEdgeLayout] of doc.layout.edges.entries()) {
      if (cadetDoc.graph.hasEdge(edgeId)) {
        cadetDoc.setEdgePath(edgeId, runtimeEdgeLayout.path);
      }
    }

    // 6. Copy Images (Document-level :IMAGE directives)
    if (doc.images.size > 0) {
      for (const [imageId, src] of doc.images.entries()) {
        // The intermediate CadetDoc will write these via CadetWriter
        cadetDoc.setImage(imageId, src);
      }
    }

    // 7. Return the populated intermediate CadetDoc
    return cadetDoc;
  }

  public savePretty(opts?: { layoutAtEnd?: boolean }): string {
    try {
      const cadetDoc = Doc.docToCadetDoc(this);
      return new CadetWriter(cadetDoc, { layoutAtEnd: opts?.layoutAtEnd }).pretty;
    } catch (e) {
      console.error('Doc.savePretty error:', e);
      return '%CADET-1.0; /* SAVE ERROR */';
    }
  }

  public saveCompact(opts?: { layoutAtEnd?: boolean }): string {
    try {
      const cadetDoc = Doc.docToCadetDoc(this);
      return new CadetWriter(cadetDoc, { layoutAtEnd: opts?.layoutAtEnd }).compact;
    } catch (e) {
      console.error('Doc.saveCompact error:', e);
      return '%CADET-1.0; /* SAVE ERROR */';
    }
  }

  // (removed applyIncludeDefaultsIfNeeded; new docs are created from DSL that already includes prelude)

  /* ------------------------------------------------------------------ *
   *  Image loading with progress reporting                              *
   * ------------------------------------------------------------------ */
  /**
   * Loads all ImageSources owned by this Doc with optional concurrency and progress reporting.
   * - Skips sources that are already loaded.
   * - Never performs loads if there are no images.
   * - Returns a summary including loaded/skipped/failed IDs.
   */
  async loadImages(opts?: {
    concurrency?: number;
    onProgress?: (p: {
      total: number;
      completed: number;
      succeeded: number;
      failed: number;
      skipped: number;
      id?: string;
      status: ImageLoadStatus;
    }) => void;
    signal?: AbortSignal;
  }): Promise<{
    total: number;
    loadedIds: string[];
    skippedIds: string[];
    failed: { id: string; error: Error }[];
  }> {
    const total = this.images.size;
    const loadedIds: string[] = [];
    const skippedIds: string[] = [];
    const failed: { id: string; error: Error }[] = [];

    let completed = 0;
    let succeeded = 0;
    let skipped = 0;
    let failedCount = 0;

    const report = (update: { id?: string; status: ImageLoadStatus }) => {
      opts?.onProgress?.({
        total,
        completed,
        succeeded,
        failed: failedCount,
        skipped,
        id: update.id,
        status: update.status
      });
    };

    if (total === 0) {
      report({ status: ImageLoadStatus.SKIPPED });
      return { total, loadedIds, skippedIds, failed };
    }

    const concurrency = Math.max(1, Math.min(opts?.concurrency ?? 4, 16));
    const queue: [string, ImageSource][] = Array.from(this.images.entries());

    // Process already-loaded upfront as skips
    const remaining: [string, ImageSource][] = [];
    for (const [id, src] of queue) {
      if (src.isLoaded) {
        skippedIds.push(id);
        skipped++;
        completed++;
        report({ id, status: ImageLoadStatus.SKIPPED });
      } else {
        remaining.push([id, src]);
      }
    }

    if (opts?.signal?.aborted) {
      return { total, loadedIds, skippedIds, failed };
    }

    let index = 0;
    const workers: Promise<void>[] = [];

    const next = async (): Promise<void> => {
      if (opts?.signal?.aborted) return;
      const i = index++;
      if (i >= remaining.length) return;

      const [id, src] = remaining[i];
      try {
        report({ id, status: ImageLoadStatus.STARTED });
        await src.load();
        loadedIds.push(id);
        succeeded++;
        completed++;
        report({ id, status: ImageLoadStatus.LOADED });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        failed.push({ id, error: err });
        failedCount++;
        completed++;
        report({ id, status: ImageLoadStatus.ERROR });
      }

      // Continue with next task in this worker
      await next();
    };

    for (let w = 0; w < Math.min(concurrency, remaining.length); w++) {
      workers.push(next());
    }

    await Promise.all(workers);

    return { total, loadedIds, skippedIds, failed };
  }

  // --- Includes setter that applies include bundles for programmatic docs ---
  get includes(): string[] {
    return this._includes;
  }
  set includes(v: string[]) {
    this._includes = [...v];
    if (this._includes.length === 0) return;
    // Apply include bundles into runtime classes so programmatic docs have defaults
    const includeLines = this._includes.map(sym => `:INCLUDE ${sym}`).join('\n');
    const dsl = `%CADET-1.0\n\n${includeLines}\n`;
    const reader = new CadetReader(dsl);
    const source = reader.doc;
    for (const className of source.classes.tree.nodes) {
      let parent: string | undefined = undefined;
      try {
        parent = source.classes.tree.parent(className);
      } catch {
        parent = undefined;
      }
      const attrs = immerClone(source.classes.tree.nodeData(className).attrs);
      if (this.classes.hasClass(className)) {
        if (Object.keys(attrs).length > 0) {
          if (this.classes.isBuiltInClass(className)) {
            this.classes.mergeIncludeClassAttrs(className, attrs);
          } else {
            this.classes.updateClassAttrs(className, attrs);
          }
        }
      } else {
        this.classes.addClass(className, parent, attrs);
      }
    }
    this.includeClassOrigins = new Map(source.includeClassOrigins);
  }
} // End Doc class
